// Analytics -> Reporting (owner, 2026-09-10). Set criteria, click Submit, get a
// PDF: a cover listing of the matched invoices with the page each starts on,
// then those invoices' own document pages.
//
// THE SERVER SELECTS; THE BROWSER ASSEMBLES. `POST /reports` returns the matched
// invoices with the page span each occupies inside its upload packet, and
// `lib/reportPdf.ts` fetches the packets, cuts the spans and draws the cover
// (`docs/api.md` §16a). Nothing is stored: the report exists in this tab and in
// the user's Downloads folder, and the server's only trace is the audit row.
//
// NO TITLE OR SUBHEADER OF ITS OWN. `AnalyticsScreen.tsx` renders both above the
// tab bar, the References and Rules arrangement (owner, 2026-09-10).
//
// THREE SECTIONS, and the split is the owner's: what a filter is ABOUT decides
// where it sits. Invoice-Level Filters ask about the invoice header; Line
// Item-Level Filters ask about its lines, and therefore match an invoice when
// ONE line satisfies all of them; Current State asks where the invoice is now
// and what is flagged on it.
//
// EVERY CONTROL IS ONE GRID CELL AND ONE CONTROL TALL. No `hint` props on the
// Fields — a hint under one control and not its neighbour is exactly what made
// the first version's rows different heights and its labels stop lining up.
// Guidance that has to be said goes in a caption under the whole section.
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react'
import {
  Body1,
  Button,
  Caption1,
  Field,
  Input,
  ProgressBar,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  Tooltip,
  TableRow,
  Toast,
  ToastTitle,
  makeStyles,
  mergeClasses,
  tokens,
  useToastController,
} from '@fluentui/react-components'
import { MultiSelectField } from '../components/MultiSelectField'
import { ErrorBanner } from '../components/States'
import { errorMessage } from '../api/errors'
import {
  REPORT_INVOICE_CAP,
  type ReportCriteria,
  type ReportInvoiceRow,
  type ReportRequestSummary,
} from '../api/types'
import { TOASTER_ID } from '../lib/constants'
import {
  FLAG_OPTIONS,
  HELD_OPTIONS,
  STATUS_OPTIONS,
  STATUS_STORED_COUNT,
  emptyCriteria,
  toReportCriteria,
  type Criteria,
} from '../lib/criteria'
import { createReport, getReportContents, getUploadPdfUrl, listReports } from '../lib/data'
import { useCriteriaOptions } from '../lib/criteriaOptions'
import type { Session } from '../lib/identity'
import { useMoneyInputStyles } from '../lib/theme'
import { describeReportCriteria } from '../lib/reportCriteria'
import {
  ReportAssemblyError,
  assembleReport,
  runOnPhrase,
  saveReport,
  type AssembleProgress,
} from '../lib/reportPdf'
import { omittedCompanyFlagLines } from '../lib/reportLayout'

const useStyles = makeStyles({
  // THE FORM FILLS THE PAGE (owner, 2026-09-30, D-251: "expand the fields in the
  // Analytics > Reporting tab, so they fit the width of the monitor"). It was
  // capped at 1080px, which is about half of a wide monitor. The cap is gone;
  // the grid below adds columns as the window widens instead of stretching two.
  form: {},
  section: { marginBottom: '24px' },
  sectionTitle: { display: 'block', marginBottom: '10px' },
  // "In line with" the heading (owner, 2026-09-11) — same line where the window
  // is wide enough, wrapping under it where it is not, which is what a heading
  // plus a three-line sentence needs. `colorNeutralForeground3`, the screen's
  // one help-text colour (see `anyHint`).
  sectionHint: {
    color: tokens.colorNeutralForeground3,
    marginInlineStart: '12px',
    fontWeight: tokens.fontWeightRegular,
  },
  // Three equal columns that can actually shrink — `minmax(0, 1fr)`, not `1fr`,
  // or a long option label forces its column wider and the grid stops aligning.
  // `alignItems: end` keeps every control on one baseline even where a label
  // wraps to two lines.
  // TWO FIELDS TO A ROW (owner, 2026-09-10). `minmax(0, 1fr)`, not `1fr`, or a
  // long option label forces its column wider and the grid stops aligning.
  // `alignItems: end` keeps every control on one baseline even where a label
  // wraps. Two columns in EVERY section, not two here and three there: it puts
  // each range's start beside its end, sits the two Flags selectors inline as
  // the owner asked, and one column count across the whole form is what makes
  // it read as one grid rather than three.
  //
  // MORE COLUMNS ON A WIDER WINDOW (owner, 2026-09-30, D-251). Two columns
  // across a whole wide monitor would make each field absurdly long, so the
  // count steps up with the window: one on a phone, two by default, four from
  // 1440px, six from 3000px. Always an EVEN count above one, so each range's
  // start still sits beside its end and the two Flags selectors stay a pair —
  // every pair starts at an even position in its section.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    columnGap: '16px',
    rowGap: '12px',
    alignItems: 'end',
    '@media (max-width: 639px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
    '@media (min-width: 1440px)': { gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' },
    '@media (min-width: 3000px)': { gridTemplateColumns: 'repeat(6, minmax(0, 1fr))' },
  },
  control: { minWidth: 0, width: '100%' },
  // ONE HINT COLOUR ON THIS SCREEN (owner, 2026-09-11: "'Any' and 'mm/dd/yyyy'
  // inside the date inputs have different coloring. They should match each
  // other, and they should match all other help text on-screen").
  //
  // They were three colours in one field. "Any" was `colorNeutralForeground4`,
  // the browser painted `mm/dd/yyyy` in its own placeholder grey, and every
  // other hint on the screen — the subheader, the `note` line below, the
  // `criteriaCell` text — is `colorNeutralForeground3`. So the two halves of a
  // single empty date field disagreed with each other AND with the rest of the
  // form. `colorNeutralForeground3` is the one the rest of the app already uses
  // for help text, so it is the one the other two moved to.
  anyHint: { color: tokens.colorNeutralForeground3 },
  // THE BROWSER'S HALF. `<input type="date">` renders `mm/dd/yyyy` itself, from
  // pseudo-elements, and there is no placeholder to colour — so this paints the
  // whole datetime-edit region and is applied ONLY while the field is empty
  // (see the call sites). Once a date is in it the class comes off and the
  // value goes back to normal input text, which is what it now is.
  //
  // `& input::…`, not a bare `::…`: Fluent puts `className` on the Input's root
  // <span>, and the native control is a child of it — the same selector shape
  // `FilterButton.tsx`'s `dateInput` has used since it shipped.
  datePlaceholder: {
    '& input::-webkit-datetime-edit': { color: tokens.colorNeutralForeground3 },
  },
  actions: { display: 'flex', gap: '12px', alignItems: 'center', margin: '8px 0' },
  progress: { maxWidth: '420px' },
  note: { color: tokens.colorNeutralForeground3, display: 'block', marginTop: '4px' },
  recent: { marginTop: '32px', maxWidth: '1080px' },
  criteriaCell: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  // ONE LINE, cut with an ellipsis, the whole text on hover (owner, 2026-09-29,
  // D-203: "Make the Criteria values a single line that uses an ellipsis if it
  // runs beyond the allowable width of the page, with a tooltip that goes the
  // full criteria on hover"). A block so the ellipsis has a width to cut at; the
  // table's `table-layout: fixed` gives the column that width.
  criteriaText: { display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // THE FIRST COLUMN since 2026-09-29 (owner, D-203: "put the download button
  // on the left end of the row"). Fixed wide enough for the busy state,
  // "Downloading…", so the row does not shift when a download starts.
  downloadHead: { width: '130px' },
  downloadCell: { whiteSpace: 'nowrap' },
  // The run time reads as the sentence the cover page uses, not a locale dump.
  //
  // WHY THE COLUMN NEEDS AN EXPLICIT WIDTH (QA sweep finding B2-02,
  // 2026-09-22). Fluent's `Table` root sets `table-layout: fixed`, so a column
  // with no declared width gets an equal share of the table — a fifth here. At
  // an ordinary 1171px laptop width that is narrower than
  // `September 22nd, 2026 at 6:07 AM (Eastern)`, and `nowrap` with nothing
  // clipping it meant the sentence ran under the `By` column and collided with
  // the name. The header cells below now declare their widths, so `fixed` has
  // something real to lay out; `ellipsis` is the backstop for a genuinely narrow
  // pane, where truncating inside the column beats overrunning the next one.
  runCell: { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  // Same treatment for the name beside it, for the same reason.
  byCell: { overflow: 'hidden', textOverflow: 'ellipsis' },
  runHead: { width: '260px' },
  byHead: { width: '170px' },
  invoicesHead: { width: '80px' },
})

/**
 * What an EMPTY date field shows before the browser's own `mm/dd/yyyy`
 * (owner, 2026-09-10).
 *
 * A native `<input type="date">` ignores `placeholder` — the `mm/dd/yyyy` is the
 * control's own rendering and cannot be replaced. `contentBefore` puts "Any" in
 * front of it inside the field, the same slot the money inputs put their `$` in.
 *
 * ONLY WHILE EMPTY: "Any 08/01/2026" would read as a value, not as a hint.
 */
function anyPrefix(value: string, className: string): ReactElement | null {
  return value === '' ? <span className={className}>Any</span> : null
}

/**
 * The criteria as the cover page should PRINT them: only the ones that actually
 * narrow the report (owner, 2026-09-10 — *"we can probably assume that it is
 * every company unless otherwise stated"*).
 *
 * A list that selects the whole available set is not a filter, it is the
 * default, and printing all 38 company names to say "no company filter" buries
 * the criteria that do matter. So a list whose selection covers every option is
 * dropped from the DISPLAY.
 *
 * WHAT IS SENT AND WHAT IS RECORDED ARE UNCHANGED. This strips the copy the
 * cover prints, never the criteria: the `report_requests` row still holds every
 * id the user picked, because the audit record's job is to say exactly what was
 * asked for.
 *
 * Vendors is never stripped, and cannot be: it is searched on the server, so
 * there is no known total to compare against — and picking some vendors is
 * always limiting anyway.
 */
function limitingCriteria(
  criteria: ReportCriteria,
  totals: { companies: number; accounts: number; departments: number; codes: number },
): ReportCriteria {
  const out = { ...criteria }
  const dropIfWhole = (key: keyof ReportCriteria, total: number): void => {
    const picked = out[key]
    if (Array.isArray(picked) && total > 0 && picked.length >= total) delete out[key]
  }
  dropIfWhole('company_id', totals.companies)
  dropIfWhole('account_id', totals.accounts)
  dropIfWhole('department_id', totals.departments)
  dropIfWhole('code_id', totals.codes)
  dropIfWhole('status', STATUS_STORED_COUNT)
  dropIfWhole('flag_type', FLAG_OPTIONS.length)
  dropIfWhole('flag_type_any', FLAG_OPTIONS.length)
  return out
}

interface Props {
  session: Session
  refreshNonce?: number
  /**
   * The criteria, SHARED WITH THE DASHBOARD (owner, 2026-09-15).
   *
   * Both tabs ask the same fifteen questions, so they now hold one answer
   * between them: narrow the Dashboard until it shows something worth reading,
   * switch to Reporting, and the form is already filled in — Submit is the only
   * thing left to do. `AnalyticsScreen.tsx` owns the state; this form and the
   * Dashboard's Filters popover are two editors of it.
   *
   * Defaulted so a test or a future caller can mount this screen on its own and
   * get the old self-contained behaviour.
   */
  criteria?: Criteria
  onCriteriaChange?: (next: Criteria) => void
}

export function ReportingScreen({ session, refreshNonce, criteria, onCriteriaChange }: Props) {
  const s = useStyles()
  const ms = useMoneyInputStyles()
  // Every other action screen confirms through the shell's toaster; Reporting
  // was the one that did not. Success and the nothing-matched outcome toast;
  // a FAILURE stays an inline banner, which is the same split
  // `RemindersScreen.tsx` uses — an error the user has to act on should not
  // time out on its own.
  const { dispatchToast } = useToastController(TOASTER_ID)
  // `useCallback` because `deliver` below depends on it: a fresh function each
  // render would rebuild `deliver` each render too, which the hooks lint calls
  // out and which defeats the memo.
  const toast = useCallback(
    (message: string, intent: 'success' | 'info') =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent },
      ),
    [dispatchToast],
  )
  // The shared state when there is one, a local one when this screen is mounted
  // alone. `useState` still runs either way — hooks are not conditional — and
  // its value is simply unused in the shared case.
  const [ownForm, setOwnForm] = useState<Criteria>(criteria ?? emptyCriteria)
  const form = criteria ?? ownForm
  const setForm = useCallback(
    (next: Criteria | ((prev: Criteria) => Criteria)): void => {
      const resolve = (prev: Criteria): Criteria =>
        typeof next === 'function' ? (next as (p: Criteria) => Criteria)(prev) : next
      if (onCriteriaChange !== undefined) onCriteriaChange(resolve(form))
      else setOwnForm(resolve)
    },
    [form, onCriteriaChange],
  )
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState<AssembleProgress | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [recent, setRecent] = useState<ReportRequestSummary[]>([])
  // Which recorded report is being downloaded, so only THAT row's button shows
  // a spinner. A single `busy` flag would disable the whole listing.
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const set = <K extends keyof Criteria>(key: K, value: Criteria[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }))

  // THE OPTION LISTS AND THE VENDOR SEARCH ARE SHARED (owner, 2026-09-14). The
  // dashboard's Filters button asks the same fifteen questions this form does,
  // so the five reads behind them live in one hook rather than once here and
  // once there.
  // Only the Reporting seats' companies (D-258).
  const options = useCriteriaOptions(session.analyticsCompanies.reporting)
  const { labels } = options

  const loadRecent = useCallback(async () => {
    try {
      setRecent(await listReports())
    } catch {
      // The audit list is context, not the feature. A failure here must not
      // block running a report.
    }
  }, [])

  useEffect(() => {
    void loadRecent()
  }, [loadRecent, refreshNonce])

  /**
   * Assemble a set of invoices into a PDF and save it — shared by Submit and by
   * the listing's Download, so the two produce the same document and the same
   * confirmation. The only differences are what the cover says about WHEN
   * (`downloadedAt`) and whether any of the recorded set is missing
   * (`unavailableCount`).
   */
  const deliver = useCallback(
    async (args: {
      invoices: readonly ReportInvoiceRow[]
      criteria: ReportCriteria
      requestedBy: string
      requestedAt: string
      downloadedAt?: string
      omittedCompanyFlagCount?: number
    }): Promise<void> => {
      // The criteria the SERVER normalized and recorded, not the form's own —
      // so the cover page and the audit row describe the same report.
      const criteriaLines = describeReportCriteria(
        limitingCriteria(args.criteria, {
          companies: options.companies.length,
          accounts: options.accounts.length,
          departments: options.departments.length,
          codes: options.codes.length,
        }),
        labels,
      )

      const assembled = await assembleReport({
        invoices: args.invoices,
        criteriaLines,
        requestedBy: args.requestedBy,
        requestedAt: args.requestedAt,
        downloadedAt: args.downloadedAt,
        omittedCompanyFlagCount: args.omittedCompanyFlagCount,
        onProgress: setProgress,
        // One retry, then the failure stops the report (D-353): every invoice
        // listed is one whose document this caller may open, so a failure is a
        // fault to see, not a permission outcome to list around.
        fetchUpload: async (uploadId) => {
          for (let attempt = 1; ; attempt++) {
            try {
              const { url } = await getUploadPdfUrl(uploadId)
              const response = await fetch(url)
              if (!response.ok) throw new Error(`The document answered ${response.status}.`)
              return new Uint8Array(await response.arrayBuffer())
            } catch (error) {
              if (attempt >= 2) throw error
            }
          }
        },
      })

      saveReport(assembled)
      // COMPLETE SENTENCES (owner, 2026-09-10). Most of this app's toasts are
      // one-verb confirmations; this one reports three facts at once — a file
      // name and two counts — and a fragment carrying three facts reads as a log
      // line rather than as a confirmation.
      //
      // Each trailing clause is its own sentence and avoids opening on a
      // numeral, which is why they say "of them" rather than repeating the noun.
      const invoices = args.invoices.length
      const pages = assembled.totalPages
      const missing = assembled.missingPageCount
      const missingSentence =
        missing === 0
          ? ''
          : missing === 1
            ? ' Source pages are missing for one of them, as the cover explains.'
            : ` Source pages are missing for ${missing} of them, as the cover explains.`
      toast(
        `Your report was downloaded as ${assembled.fileName}. ` +
          `It covers ${invoices} invoice${invoices === 1 ? '' : 's'} across ` +
          `${pages} page${pages === 1 ? '' : 's'}.${missingSentence}`,
        'success',
      )
    },
    [options, labels, toast],
  )

  /**
   * DOWNLOAD, not Run (owner, 2026-09-14): re-read a recorded report's exact
   * invoices by the ids it stored, rather than running its criteria again.
   *
   * The cover names the ORIGINAL run — who and when — because that is what the
   * criteria on it describe. A second line says when this copy was taken, since
   * the values in it are today's and only the SET is pinned.
   */
  const downloadAgain = async (entry: ReportRequestSummary): Promise<void> => {
    setDownloadingId(entry.report_id)
    setError(null)
    setProgress(null)
    try {
      const contents = await getReportContents(entry.report_id)
      if (contents.invoices.length === 0) {
        // Only reachable for a report that matched nothing when it ran — those
        // are recorded too, because an empty report is a report someone ran.
        toast('That report covered no invoices, so there is nothing to download.', 'info')
        return
      }
      await deliver({
        invoices: contents.invoices,
        criteria: contents.criteria,
        // The ORIGINAL requester's name, falling back to their id, so the cover
        // names a person even on a row written before the column existed.
        requestedBy: contents.requested_by_name ?? contents.requested_by,
        requestedAt: contents.requested_at,
        downloadedAt: new Date().toISOString(),
      })
    } catch (e) {
      setError(e instanceof ReportAssemblyError ? e.message : errorMessage(e))
    } finally {
      setDownloadingId(null)
      setProgress(null)
    }
  }

  const submit = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    setProgress(null)
    try {
      const report = await createReport(toReportCriteria(form))
      if (report.invoice_count === 0) {
        // Matches the runner cannot see yet still say so (D-354), or "no
        // invoices match" would be false.
        toast(
          ['No invoices match those criteria. Nothing was downloaded.', ...omittedCompanyFlagLines(report.omitted_company_flag_count)].join(' '),
          'info',
        )
        void loadRecent()
        return
      }

      await deliver({
        invoices: report.invoices,
        criteria: report.criteria,
        requestedBy: session.user.fullName,
        requestedAt: report.requested_at,
        omittedCompanyFlagCount: report.omitted_company_flag_count,
      })
      void loadRecent()
    } catch (e) {
      setError(e instanceof ReportAssemblyError ? e.message : errorMessage(e))
    } finally {
      setBusy(false)
      setProgress(null)
    }
  }

  const progressLabel = useMemo(() => {
    if (progress === null) return 'Selecting invoices…'
    if (progress.phase === 'fetching') {
      return `Fetching document ${progress.done} of ${progress.total}…`
    }
    return `Assembling page ${progress.done} of ${progress.total}…`
  }, [progress])

  return (
    <div>
      <div className={s.form}>
        <div className={s.section}>
          <Body1 className={s.sectionTitle}>
            <strong>Invoice-Level Filters</strong>
          </Body1>
          <div className={s.grid}>
            <MultiSelectField
              label="Companies"
              options={options.companies}
              selected={form.companies}
              onChange={(v) => set('companies', v)}
            />
            <MultiSelectField
              label="Vendors"
              search={options.searchVendorOptions}
              selected={form.vendors}
              onChange={(v) => set('vendors', v)}
            />
            <Field label="Uploaded Date From">
              <Input
                className={mergeClasses(s.control, form.uploadedStart === '' && s.datePlaceholder)}
                type="date"
                contentBefore={anyPrefix(form.uploadedStart, s.anyHint)}
                value={form.uploadedStart}
                onChange={(_, d) => set('uploadedStart', d.value)}
              />
            </Field>
            <Field label="Uploaded Date To">
              <Input
                className={mergeClasses(s.control, form.uploadedEnd === '' && s.datePlaceholder)}
                type="date"
                contentBefore={anyPrefix(form.uploadedEnd, s.anyHint)}
                value={form.uploadedEnd}
                onChange={(_, d) => set('uploadedEnd', d.value)}
              />
            </Field>
            <Field label="Invoice Date From">
              <Input
                className={mergeClasses(s.control, form.invoiceStart === '' && s.datePlaceholder)}
                type="date"
                contentBefore={anyPrefix(form.invoiceStart, s.anyHint)}
                value={form.invoiceStart}
                onChange={(_, d) => set('invoiceStart', d.value)}
              />
            </Field>
            <Field label="Invoice Date To">
              <Input
                className={mergeClasses(s.control, form.invoiceEnd === '' && s.datePlaceholder)}
                type="date"
                contentBefore={anyPrefix(form.invoiceEnd, s.anyHint)}
                value={form.invoiceEnd}
                onChange={(_, d) => set('invoiceEnd', d.value)}
              />
            </Field>
            <Field label="Exported Date From">
              <Input
                className={mergeClasses(s.control, form.exportedStart === '' && s.datePlaceholder)}
                type="date"
                contentBefore={anyPrefix(form.exportedStart, s.anyHint)}
                value={form.exportedStart}
                onChange={(_, d) => set('exportedStart', d.value)}
              />
            </Field>
            <Field label="Exported Date To">
              <Input
                className={mergeClasses(s.control, form.exportedEnd === '' && s.datePlaceholder)}
                type="date"
                contentBefore={anyPrefix(form.exportedEnd, s.anyHint)}
                value={form.exportedEnd}
                onChange={(_, d) => set('exportedEnd', d.value)}
              />
            </Field>
            {/* THE SAME MONEY INPUT EVERY OTHER AMOUNT FIELD USES —
                `type="number" contentBefore="$"` with `moneyText` (right-aligned,
                tabular digits, no spinners), exactly as `FilterButton.tsx`'s own
                total range does it. The first version was a plain text box, so it
                accepted letters; there was never a reason for this field to
                differ from every other amount in the app. */}
            <Field label="Total From">
              <Input
                className={s.control}
                type="number"
                contentBefore="$"
                input={{ className: ms.moneyText }}
                value={form.totalStart}
                placeholder="Any"
                onChange={(_, d) => set('totalStart', d.value)}
              />
            </Field>
            <Field label="Total To">
              <Input
                className={s.control}
                type="number"
                contentBefore="$"
                input={{ className: ms.moneyText }}
                value={form.totalEnd}
                placeholder="Any"
                onChange={(_, d) => set('totalEnd', d.value)}
              />
            </Field>
          </div>
        </div>

        <div className={s.section}>
          {/* THE ONE THING ABOUT THIS SECTION THAT SURPRISES PEOPLE (owner,
              2026-09-11, verbatim). Every other filter on this form narrows
              which INVOICES come back; these three narrow which invoices
              QUALIFY, and then the whole invoice is produced. Someone filtering
              Accounts for Uniforms is not asking for a file of uniform lines,
              and the file they get will not look like one — so the sentence
              sits beside the heading rather than under any one control, because
              it is true of all three. */}
          <Body1 className={s.sectionTitle}>
            <strong>Line Item-Level Filters</strong>
            <Caption1 className={s.sectionHint}>
              Every invoice that contains your selected filters will be produced in full. For
              example, filtering Accounts for &ldquo;Uniforms&rdquo; below will produce a group of
              invoices that include Uniforms, but those invoices may also include other additional
              coding.
            </Caption1>
          </Body1>
          <div className={s.grid}>
            <MultiSelectField
              label="Accounts"
              options={options.accounts}
              selected={form.accounts}
              onChange={(v) => set('accounts', v)}
            />
            <MultiSelectField
              label="Departments"
              options={options.departments}
              selected={form.departments}
              onChange={(v) => set('departments', v)}
            />
            <MultiSelectField
              label="Codes"
              options={options.codes}
              selected={form.codes}
              onChange={(v) => set('codes', v)}
            />
          </div>
        </div>

        <div className={s.section}>
          <Body1 className={s.sectionTitle}>
            <strong>Current State</strong>
          </Body1>
          <div className={s.grid}>
            <MultiSelectField
              label="Status"
              options={STATUS_OPTIONS}
              selected={form.statuses}
              onChange={(v) => set('statuses', v)}
            />
            {/* Held and Not Held, either or both; both is Any (owner, 2026-10-14). */}
            <MultiSelectField
              label="On Hold"
              options={HELD_OPTIONS}
              selected={form.held}
              onChange={(v) => set('held', v)}
            />
            <MultiSelectField
              label="Total Flags"
              options={FLAG_OPTIONS}
              selected={form.totalFlags}
              onChange={(v) => set('totalFlags', v)}
            />
            <MultiSelectField
              label="Unresolved Flags"
              options={FLAG_OPTIONS}
              selected={form.unresolvedFlags}
              onChange={(v) => set('unresolvedFlags', v)}
            />
          </div>
        </div>

        <div className={s.actions}>
          <Button appearance="primary" disabled={busy} onClick={() => void submit()}>
            Submit
          </Button>
          <Button appearance="secondary" disabled={busy} onClick={() => setForm(emptyCriteria)}>
            Clear
          </Button>
          {busy && <Spinner size="tiny" label={progressLabel} />}
        </div>

        {busy && progress !== null && progress.total > 0 && (
          <ProgressBar
            className={s.progress}
            value={progress.done / progress.total}
            thickness="large"
          />
        )}

        <ErrorBanner message={error} />
        <Caption1 className={s.note}>
          A report includes at most {REPORT_INVOICE_CAP} invoices. Requests for larger reports
          will be refused.
        </Caption1>
      </div>

      {recent.length > 0 && (
        <div className={s.recent}>
          <Body1>
            <strong>Recent Reports</strong>
          </Body1>
          <Table size="small" aria-label="Recent reports">
            <TableHeader>
              <TableRow>
                <TableHeaderCell className={s.downloadHead} aria-label="Download" />
                <TableHeaderCell className={s.runHead}>Run</TableHeaderCell>
                <TableHeaderCell className={s.byHead}>By</TableHeaderCell>
                <TableHeaderCell className={s.invoicesHead}>Invoices</TableHeaderCell>
                <TableHeaderCell>Criteria</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recent.map((entry) => (
                <TableRow key={entry.report_id}>
                  <TableCell className={s.downloadCell}>
                    {/* DOWNLOAD, not Run (owner, 2026-09-14) — the word is the
                        promise: this re-reads the invoices that report covered,
                        rather than running its criteria against today's data.
                        `busy` disables it too, so a download cannot start on top
                        of a report still assembling. */}
                    <Button
                      size="small"
                      appearance="secondary"
                      disabled={busy || downloadingId !== null}
                      onClick={() => void downloadAgain(entry)}
                      title="Download these same invoices again"
                    >
                      {downloadingId === entry.report_id ? (
                        <Spinner size="tiny" label="Downloading…" />
                      ) : (
                        'Download'
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className={s.runCell}>{runOnPhrase(entry.requested_at)}</TableCell>
                  {/* The NAME, not the Entra object id (owner, 2026-09-14:
                      "By shows a GUID, rather than a name"). It is denormalized
                      onto the audit row at write time, so this needs no lookup
                      and still reads correctly after someone leaves the
                      directory. The id remains the fallback for a row written
                      before the column existed, and is on the title either way
                      so an auditor can still identify the subject exactly. */}
                  <TableCell className={s.byCell} title={entry.requested_by}>
                    {entry.requested_by_name ?? entry.requested_by}
                  </TableCell>
                  <TableCell>{entry.invoice_count}</TableCell>
                  <TableCell className={s.criteriaCell}>
                    {(() => {
                      const text = describeReportCriteria(entry.criteria, labels).join(' · ')
                      return (
                        <Tooltip content={text} relationship="description">
                          <span className={s.criteriaText}>{text}</span>
                        </Tooltip>
                      )
                    })()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  )
}
